import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBeforeUnload, useBlocker, type BlockerFunction } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { nanoid } from 'nanoid'

import {
  FileInfo,
  uploadFileWithProgress as _uploadFileWithProgress,
  useGetFileByIdQuery,
  useLazyGetFileByIdQuery,
  useUploadFileRequestMutation,
} from '../../store/media/file'
import { useDeleteFileMutation, useUpdateFileMutation } from '../../store/media/media'
import { useGetRetentionPoliciesQuery } from '../../store/account/account'
import { selectDocumentBatch } from '../../store/processDocument/documentBatch'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useListAiAgentResultQuery } from '../../store/ai/aiAgentResult'
import { useGetMeQuery } from '../../store/user/user'
import { AIAgentType } from '../../models/AIAgent'

import { FileKind, FileStatus } from '../../models/File'
import { RetentionResourceType, RetentionRuleType } from '../../models/RetentionPolicy'

import { type IntegrationServiceId } from '../../common/integrations'
import { getConnectedDocumentServiceIds } from '../../common/getConnectedDocumentServiceIds'
import { type FileBrowserItem } from '../../components/DocumentFileBrowser/types'

import { DocumentUploadSource, hashFile, MAX_DOCUMENT_BATCH_FILES } from './DocumentUpload/utils'
import { DocumentFile, DocumentFileType } from '../../models/Document'
import {
  DocumentConnectorItem,
  useGetDocumentConnectorFileQuery,
  useLazyGetDocumentConnectorFileQuery,
} from '../../store/documentConnectors/documentConnectors'
import { DocumentFileInfo, selectDocument } from '../../store/processDocument/document'
import { skipToken } from '@reduxjs/toolkit/query'

export interface ZocksDocumentUploadQueueItem {
  source: DocumentUploadSource.ZOCKS
  id: string
  file: File
  isUploading: boolean
  abortController: AbortController
  fileInfo?: FileInfo
  progress?: number
  hash: string
}

export interface IntegrationDocumentUploadQueueItem {
  source: DocumentUploadSource.INTEGRATION
  id: string
  serviceId: IntegrationServiceId
  fileId: string
  name: string
  size: number
}

export type DocumentUploadQueueItem = ZocksDocumentUploadQueueItem | IntegrationDocumentUploadQueueItem

export interface SuccessfulZocksDocumentUploadQueueItem {
  source: DocumentUploadSource.ZOCKS
  fileInfo: FileInfo
  hash?: string
}

export interface SuccessfulIntegrationDocumentUploadQueueItem {
  source: DocumentUploadSource.INTEGRATION
  serviceId: IntegrationServiceId
  fileId: string
  name: string
  size: number
}

export type SuccessfulDocumentUploadQueueItem = SuccessfulZocksDocumentUploadQueueItem | SuccessfulIntegrationDocumentUploadQueueItem

export const useDocumentFileUpload = () => {
  const accountId = useAppSelector(selectAccountId)

  const [triggerUploadFileRequest] = useUploadFileRequestMutation()
  const [triggerUpdateFile] = useUpdateFileMutation()
  const [triggerDeleteFile] = useDeleteFileMutation()

  const [uploadQueueItems, setUploadQueueItems] = useState<DocumentUploadQueueItem[]>([])

  const onProgress = (itemId: string, progress: number) => {
    setUploadQueueItems((prev) => prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, progress } : item)))
  }

  const onSuccess = (itemId: string, fileInfo: FileInfo) => {
    setUploadQueueItems((prev) =>
      prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, fileInfo, isUploading: false } : item)),
    )
  }

  const onError = (itemId: string) => {
    setUploadQueueItems((prev) =>
      prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, isUploading: false } : item)),
    )
  }

  const onClear = async (itemId: string) => {
    const item = uploadQueueItems.find((item) => item.id === itemId)
    if (!item) {
      return
    }

    setUploadQueueItems((prev) => prev.filter((item) => item.id !== itemId))

    if (item.source !== DocumentUploadSource.ZOCKS) {
      return
    }

    try {
      const { fileInfo, abortController } = item
      if (fileInfo) {
        await triggerDeleteFile({ accountId, fileId: fileInfo.fileId })
      } else {
        abortController.abort()
      }
    } catch (e) {
      console.error(e)
    }
  }

  const onSingleFileSelected = async (file: File) => {
    const itemId = nanoid()
    const abortController = new AbortController()

    try {
      const hash = await hashFile(file)

      setUploadQueueItems((prev) => [...prev, { source: DocumentUploadSource.ZOCKS, id: itemId, isUploading: true, file, abortController, hash }])

      const fileInfo = await uploadFileWithProgress(file, (progress) => onProgress(itemId, progress), abortController.signal)

      onSuccess(itemId, fileInfo)
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      onError(itemId)
    }
  }

  const onFilesSelected = async (files: File[]) => {
    await Promise.all(files.map(onSingleFileSelected))
  }

  const onIntegrationFilesSelected = (serviceId: IntegrationServiceId, items: FileBrowserItem[]) => {
    setUploadQueueItems((prev) => {
      const remaining = MAX_DOCUMENT_BATCH_FILES - prev.length
      const newItems: IntegrationDocumentUploadQueueItem[] = items
        .filter((item) => item.type === 'file')
        .slice(0, Math.max(0, remaining))
        .map((item) => ({ source: DocumentUploadSource.INTEGRATION, id: nanoid(), serviceId, fileId: item.id, name: item.name, size: item.size ?? 0 }))

      return [...prev, ...newItems]
    })
  }

  const onRetry = async (itemId: string) => {
    const item = uploadQueueItems.find((item) => item.id === itemId)
    if (!item || item.source !== DocumentUploadSource.ZOCKS) {
      return
    }

    const abortController = new AbortController()
    setUploadQueueItems((prev) =>
      prev.map((item) =>
        item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, isUploading: true, progress: 0, abortController } : item,
      ),
    )

    try {
      const fileInfo = await uploadFileWithProgress(item.file, (progress) => onProgress(itemId, progress), abortController.signal)
      onSuccess(itemId, fileInfo)
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
      onError(itemId)
    }
  }

  const requestFileUpload = async (file: File) => {
    const body = { fileName: file.name, fileSize: file.size, contentType: file.type, kind: FileKind.DOCUMENT }
    const { data: fileUploadRequestResponse } = await triggerUploadFileRequest({ accountId, body }).unwrap()
    return {
      uploadUrl: fileUploadRequestResponse.fileUploadUrl,
      uploadParameters: fileUploadRequestResponse.fileUploadParameters,
      fileInfo: fileUploadRequestResponse.fileInfo,
    }
  }

  const uploadFileWithProgress = async (file: File, onProgress: (progress: number) => void, signal: AbortSignal) => {
    const { uploadUrl, uploadParameters, fileInfo } = await requestFileUpload(file)
    const { fileId } = fileInfo

    await _uploadFileWithProgress(uploadUrl, uploadParameters, file, onProgress, signal)

    await triggerUpdateFile({ accountId, fileId, body: { status: FileStatus.UPLOADED } }).unwrap()

    return fileInfo
  }

  const successfulUploadQueueItems = useMemo(
    () =>
      uploadQueueItems.reduce<SuccessfulDocumentUploadQueueItem[]>((acc, item) => {
        if (item.source === DocumentUploadSource.INTEGRATION) {
          const { serviceId, fileId, name, size } = item
          return [...acc, { source: DocumentUploadSource.INTEGRATION, serviceId, fileId, name, size }]
        }
        return item.fileInfo ? [...acc, { source: DocumentUploadSource.ZOCKS, fileInfo: item.fileInfo, hash: item.hash }] : acc
      }, []),
    [uploadQueueItems],
  )

  return { onFilesSelected, onIntegrationFilesSelected, uploadQueueItems, successfulUploadQueueItems, onClear, onRetry }
}

export const useSafeTimeout = () => {
  const ref = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSafeTimeout = () => {
    if (ref.current) {
      clearTimeout(ref.current)
    }
  }

  useEffect(() => {
    return () => {
      clearSafeTimeout()
    }
  }, [])

  const setSafeTimeout = (callback: () => void, delay?: number) => {
    ref.current = setTimeout(callback, delay)
  }

  return { setSafeTimeout, clearSafeTimeout }
}

export const useProcessDocumentFileInput = (supportedMimeTypes: string[], onFilesSelected: (files: File[]) => Promise<void>, disabled = false) => {
  const [isHovering, setIsHovering] = useState<boolean>(false)

  useEffect(() => {
    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
    }

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault()
    }

    window.addEventListener('drop', handleDrop)
    window.addEventListener('dragover', handleDragOver)

    return () => {
      window.removeEventListener('drop', handleDrop)
      window.removeEventListener('dragover', handleDragOver)
    }
  }, [])

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()

    if (disabled) {
      return
    }

    const input = e.target
    const { files } = input
    if (!files?.length) {
      return
    }

    const filteredFiles = Array.from(files).filter((file) => supportedMimeTypes.includes(file.type))

    input.value = ''

    if (!filteredFiles.length) {
      return
    }

    await onFilesSelected(filteredFiles)
  }

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setIsHovering(false)

    if (disabled) {
      return
    }

    const { dataTransfer } = e
    const { files } = dataTransfer
    if (!files.length) {
      return
    }

    const filteredFiles = Array.from(files).filter((file) => supportedMimeTypes.includes(file.type))
    if (!filteredFiles.length) {
      return
    }

    await onFilesSelected(filteredFiles)
  }

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (disabled) {
      return
    }

    setIsHovering(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsHovering(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return {
    isHovering,
    handleChange,
    handleDrop,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
  }
}

const connectorItemToDocumentFileInfo = (item: DocumentConnectorItem): DocumentFileInfo => ({
  fileId: item.integration?.externalId ?? item.id ?? '',
  fileName: item.name,
  fileSize: item.size ?? 0,
  contentType: item.contentType ?? '',
})

export const useDocumentFile = (documentFilesToInitialize?: DocumentFile[]) => {
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetFileById] = useLazyGetFileByIdQuery()
  const [triggerGetDocumentFileById] = useLazyGetDocumentConnectorFileQuery()

  const documentFile = documentFilesToInitialize?.[0]

  const { data: me, isFetching: meFetching } = useGetMeQuery({ accountId })

  const connectedDocumentServiceIds = useMemo(() => getConnectedDocumentServiceIds(me), [me])

  const { data: zocksFileInfo, isFetching: zocksFetching } = useGetFileByIdQuery(
    documentFile?.type === DocumentFileType.ZOCKS ? { accountId, fileId: documentFile.fileId } : skipToken,
  )
  const { data: integrationFileInfo, isFetching: integrationFetching } = useGetDocumentConnectorFileQuery(
    documentFile?.type === DocumentFileType.INTEGRATION && connectedDocumentServiceIds.includes(documentFile.connector as IntegrationServiceId)
      ? { accountId, connector: documentFile.connector, fileId: documentFile.fileId }
      : skipToken,
  )

  const fetchZocksDocumentFile = useCallback(
    async (fileId: string): Promise<FileInfo | null> => {
      try {
        return await triggerGetFileById({ accountId, fileId }).unwrap()
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerGetFileById],
  )

  const fetchIntegrationDocumentFile = useCallback(
    async (connector: string, fileId: string): Promise<DocumentFileInfo | null> => {
      try {
        const item = await triggerGetDocumentFileById({ accountId, connector, fileId }).unwrap()
        return item ? connectorItemToDocumentFileInfo(item) : null
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerGetDocumentFileById],
  )

  const fetchDocumentFile = useCallback(
    async (documentFile: DocumentFile): Promise<FileInfo | DocumentFileInfo | null> => {
      if (documentFile.type === DocumentFileType.INTEGRATION) {
        if (!connectedDocumentServiceIds.includes(documentFile.connector as IntegrationServiceId)) {
          return null
        }
        return await fetchIntegrationDocumentFile(documentFile.connector, documentFile.fileId)
      }
      return await fetchZocksDocumentFile(documentFile.fileId)
    },
    [connectedDocumentServiceIds, fetchIntegrationDocumentFile, fetchZocksDocumentFile],
  )

  const fileInfo =
    documentFile?.type === DocumentFileType.INTEGRATION
      ? integrationFileInfo
        ? connectorItemToDocumentFileInfo(integrationFileInfo)
        : integrationFileInfo
      : zocksFileInfo

  return { fetchDocumentFile, isLoading: integrationFetching || zocksFetching || meFetching, fileInfo }
}

export const useDocumentSummaryAiAgentResult = () => {
  const accountId = useAppSelector(selectAccountId)
  const document = useAppSelector(selectDocument)

  const { currentData: aiAgentResults, refetch } = useListAiAgentResultQuery(
    document ? { accountId, type: AIAgentType.DOCUMENT_SUMMARY, resultKey: document.id } : skipToken,
  )

  const refetchAiAgentResult = useCallback(async () => {
    try {
      const aiAgentResults = await refetch().unwrap()
      if (!aiAgentResults.length) {
        return null
      }
      return aiAgentResults[0]
    } catch (e) {
      console.error(e)
      return null
    }
  }, [refetch])

  return { aiAgentResult: aiAgentResults?.length ? aiAgentResults[0] : null, refetchAiAgentResult }
}

export const useAccountHasDocumentZdrPolicy = () => {
  const accountId = useAppSelector(selectAccountId)

  const { data: retentionPoliciesResponse } = useGetRetentionPoliciesQuery({ accountId })

  return useMemo(
    () =>
      !!retentionPoliciesResponse?.data.find(({ resource, rule }) => resource.type === RetentionResourceType.DOCUMENT && rule.type === RetentionRuleType.ZERO),
    [retentionPoliciesResponse],
  )
}

export const useDocumentLeaveBlocker = () => {
  const accountHasZDRPolicy = useAccountHasDocumentZdrPolicy()
  const documentBatch = useAppSelector(selectDocumentBatch)

  const allowedPathnames = useMemo(
    () => (documentBatch ? [`/document-batches/${documentBatch.id}`, ...documentBatch.documents.map((document) => `/documents/${document.id}`)] : []),
    [documentBatch],
  )

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) => {
        if (!accountHasZDRPolicy || currentLocation.pathname === nextLocation.pathname) {
          return false
        }

        return !allowedPathnames.some((pathname) => nextLocation.pathname.startsWith(pathname))
      },
      [accountHasZDRPolicy, allowedPathnames],
    ),
  )

  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (accountHasZDRPolicy) {
          e.preventDefault()
          e.returnValue = ''
        }
      },
      [accountHasZDRPolicy],
    ),
  )

  const confirmLeave = () => {
    if (blocker.state === 'blocked') {
      blocker.proceed()
    }
  }

  const cancelLeave = () => {
    if (blocker.state === 'blocked') {
      blocker.reset()
    }
  }

  return { showModal: blocker.state === 'blocked', confirmLeave, cancelLeave, isBatch: !!documentBatch }
}
